import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission, constructQuery, EventEmitter } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getProject } from 'src/api/personal/projects';
import { getEventsForProject } from 'src/api/personal/eventDetail';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import SubcontractingTab from 'src/parts/tabs/SubcontractingTab';
interface ProjectEventsSearch {
	page?: number;
	sort?: string;
}

export const Route = createFileRoute('/personal_/projects/$projectId/events')({
	validateSearch: (search: Record<string, unknown>): ProjectEventsSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
	}),
	component: ProjectEventsPage,
});

function ProjectEventsPage() {
	const { projectId } = Route.useParams();
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { config } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesTags = featureFlags?.USES_TAGS || false;

	const query = constructQuery({
		page,
		sort,
	});

	const { data: project, isLoading: projectLoading } = useQuery({
		queryKey: ['personal-project', projectId],
		queryFn: () => getProject(projectId),
	});

	const { data: eventsData, isLoading: eventsLoading } = useQuery({
		queryKey: ['personal-project-events', projectId, query],
		queryFn: () => getEventsForProject(projectId, query),
		enabled: !!project,
	});

	const { data: typesData } = useQuery({
		queryKey: ['event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
	});

	const revalidateEvents = () => {
		queryClient.invalidateQueries({ queryKey: ['personal-project-events', projectId] });
	};

	const addEvent = async (data: unknown) => {
		await request
			.post(`/api/personal-dashboard/projects/${projectId}/add-event`, data)
			.then(() => {
				EventEmitter.emit('toast', {
					type: 'success',
					message: 'Event created',
				});
			})
			.catch((error: { message?: string; body?: { message?: string } }) => {
				const errorMessage =
					error?.message || error?.body?.message || 'Failed to create Event. Did you enter all data correctly?';
				EventEmitter.emit('toast', {
					type: 'error',
					message: errorMessage,
				});
				throw error;
			});
	};

	const isLoading = authLoading || projectLoading || eventsLoading;

	if (isLoading || !authenticatedUser || !project) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const types = typesData?.data || [];
	const tags = tagsData || [];

	return (
		<SubcontractingTab
			authenticatedUser={authenticatedUser}
			events={eventsData}
			addEvent={addEvent}
			permissions={{
				view: verifyPermission([PERMISSIONS.personalAllEventsView]),
				create: verifyPermission([PERMISSIONS.personalAllEventsCreate]),
				update: verifyPermission([PERMISSIONS.personalAllEventsEdit]),
				archive: verifyPermission([PERMISSIONS.personalAllEventsArchive]),
			}}
			onReload={revalidateEvents}
			types={types}
			project={project}
			tags={tags}
			usesTags={usesTags}
			eventDetailUrl="/personal/events"
		/>
	);
}
